/**
 * Borrador de ticket compartido por los modos de creación (formulario
 * avanzado y paso a paso): valores iniciales, catálogo de
 * plataformas, validación y construcción del payload que se inserta.
 *
 * Lógica pura: sin React ni I/O.
 */

import { DEFAULT_TICKET_TYPE, TICKET_TYPES } from './ticketType'
import { derivePriorityId } from './priorityMatrix'
import { getPolicy } from './sla'

/**
 * Plataformas / sistemas de origen de un ticket (columna `systemOrigin`).
 * `value` es lo que se persiste; `label` y `icon` son para la UI.
 * @type {Array<{value: string, label: string, icon: string}>}
 */
export const TICKET_ORIGINS = [
  { value: 'Bitrix24', label: 'Bitrix24', icon: 'fa-comments' },
  { value: 'CMS', label: 'CMS', icon: 'fa-globe' },
  { value: 'Docs', label: 'Docs', icon: 'fa-file-lines' },
  {
    value: 'Equipos',
    label: 'Equipos (Laptop, Teléfono, etc)',
    icon: 'fa-laptop',
  },
  { value: 'Google Workspace', label: 'Google Workspace', icon: 'fa-envelope' },
  { value: 'IA', label: 'IA', icon: 'fa-robot' },
  { value: 'ITManager', label: 'ITManager', icon: 'fa-server' },
  { value: 'Kora', label: 'Kora', icon: 'fa-cube' },
  { value: 'KPIS', label: 'KPIS', icon: 'fa-chart-line' },
  { value: 'MyMeets', label: 'MyMeets', icon: 'fa-video' },
  { value: 'OPS', label: 'OPS', icon: 'fa-gears' },
  { value: 'Proyectos', label: 'Proyectos', icon: 'fa-diagram-project' },
  { value: 'Pulperia', label: 'Pulpería', icon: 'fa-store' },
  { value: 'Recursos Humanos', label: 'Recursos Humanos', icon: 'fa-users' },
  { value: 'SICOP', label: 'SICOP', icon: 'fa-file-contract' },
  { value: 'Teramind', label: 'Teramind', icon: 'fa-eye' },
  { value: 'Tickets', label: 'Tickets', icon: 'fa-ticket' },
]

/** Campos editables del borrador y sus valores iniciales. */
export const INITIAL_TICKET_DRAFT = {
  title: '',
  description: '',
  type: DEFAULT_TICKET_TYPE,
  systemOrigin: '',
  impact: 2,
  urgency: 2,
  priorityId: derivePriorityId(2, 2),
}

/** Largo mínimo recomendado de la descripción (sólo aviso, no bloquea). */
export const MIN_DESCRIPTION_LENGTH = 20

/**
 * Aplica cambios al borrador. Si cambia el impacto o la urgencia se recalcula
 * la prioridad (matriz Impacto × Urgencia): nunca se elige a mano.
 * @param {Object} draft
 * @param {Object} patch - Campos a cambiar.
 * @returns {Object} Borrador nuevo.
 */
export const updateDraft = (draft, patch) => {
  const next = { ...draft, ...patch }
  if ('impact' in patch || 'urgency' in patch) {
    next.impact = Number(next.impact)
    next.urgency = Number(next.urgency)
    next.priorityId = derivePriorityId(next.impact, next.urgency)
  }
  return next
}

/**
 * Valida el borrador. Devuelve un mapa campo → mensaje (vacío si es válido).
 * @param {Object} draft
 * @param {string[]} [fields] - Sólo estos campos (útil por paso del asistente).
 * @returns {Record<string, string>}
 */
export const validateDraft = (draft, fields) => {
  const errors = {}
  const check = (key) => !fields || fields.includes(key)

  if (check('title') && !draft.title?.trim())
    errors.title = 'Escriba un título que resuma el caso.'
  if (check('systemOrigin') && !draft.systemOrigin)
    errors.systemOrigin = 'Seleccione la plataforma afectada.'
  if (check('type') && !TICKET_TYPES[draft.type])
    errors.type = 'Seleccione el tipo de registro.'
  if (check('description') && !draft.description?.trim())
    errors.description = 'Describa el caso.'

  return errors
}

/**
 * Construye la fila a insertar en `Tickets.ticket` a partir del borrador.
 * Fija fechas, estado inicial y el deadline de SLA (snapshot al crear).
 *
 * @param {Object} draft - Borrador validado.
 * @param {Object} meta
 * @param {string} meta.userId - `auth.uid` del autor.
 * @param {number} [meta.statusId=1] - Estado inicial.
 * @param {Date} [meta.now=new Date()]
 * @returns {Object}
 */
export const buildTicketPayload = (
  draft,
  { userId, statusId = 1, now = new Date() }
) => {
  const priorityId = derivePriorityId(draft.impact, draft.urgency)
  const base = {
    title: draft.title.trim(),
    description: draft.description.trim(),
    type: draft.type,
    systemOrigin: draft.systemOrigin,
    impact: Number(draft.impact),
    urgency: Number(draft.urgency),
    priorityId,
  }
  // Los tipos sin SLA (p. ej. Cambios) quedan con dueAt nulo.
  const policy = getPolicy(base)

  return {
    ...base,
    userId,
    responsibleId: null,
    statusId,
    categoryId: 1,
    createdAt: now.toISOString(),
    dueAt: policy
      ? new Date(now.getTime() + policy.resolutionMins * 60000).toISOString()
      : null,
    firstResponseAt: null,
    resolvedAt: null,
    closedAt: null,
    deleted: false,
  }
}

/**
 * Arma la descripción a partir de las preguntas guiadas del modo paso a paso.
 * Omite las respuestas vacías.
 * @param {Array<{label: string, value: string}>} answers
 * @returns {string}
 */
export const composeDescription = (answers) =>
  answers
    .filter((a) => a.value?.trim())
    .map((a) => `${a.label}\n${a.value.trim()}`)
    .join('\n\n')
